import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Linkedin, Youtube, Phone, ArrowUp } from 'lucide-react';
import logo from '../assets/images/atadaboard-logo.png';
import { APP_LINKS, CONTACT, SOCIAL_LINKS } from '../data/site';

const SOCIAL_ICONS = { Instagram, Facebook, Linkedin, Youtube };

/*
  Deliberately not a four-column link farm. The footer carries the paths that
  exist: the sections of this page, the /download route, the three places you
  can sign in, and how to reach a human.

  No privacy policy or terms links appear here because no such route exists in
  this application. Add the routes first, then the links.
*/
const PAGE_LINKS = [
  { label: 'Platform', id: 'platform' },
  { label: 'Features', id: 'features' },
  { label: 'Cashless school', id: 'payments' },
  { label: 'For parents', id: 'parents' },
  { label: 'Pricing', id: 'pricing' },
  { label: 'Stories', id: 'video-testimonials' },
];

const APP_ENTRIES = [
  { label: 'Web console', href: APP_LINKS.web },
  { label: 'iOS app', href: APP_LINKS.ios },
  { label: 'Android app', href: APP_LINKS.android },
];

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const goTo = (sectionId) =>
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <footer className="ab-footer">
      <div className="ab-shell ab-footer__top">
        <div className="ab-footer__brand">
          <img src={logo} alt="AtadaBoard" width="2437" height="732" loading="lazy" />
          <p>
            School management software for schools in Ghana and across Africa, from Atada
            Technologies.
          </p>
          <a href={CONTACT.phoneHref} className="ab-footer__phone ab-num">
            <Phone size={16} strokeWidth={1.75} />
            {CONTACT.phoneDisplay}
          </a>
        </div>

        <nav className="ab-footer__nav" aria-label="Footer">
          <div className="ab-footer__group">
            <h2>Product</h2>
            <ul>
              {PAGE_LINKS.map((link) => (
                <li key={link.id}>
                  <button type="button" onClick={() => goTo(link.id)}>
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="ab-footer__group">
            <h2>Get AtadaBoard</h2>
            <ul>
              {APP_ENTRIES.map((entry) => (
                <li key={entry.label}>
                  <a href={entry.href} target="_blank" rel="noopener noreferrer">
                    {entry.label}
                  </a>
                </li>
              ))}
              <li>
                <Link to="/download">All download options</Link>
              </li>
              <li>
                <button type="button" onClick={() => goTo('demo-form')}>
                  Book a demo
                </button>
              </li>
            </ul>
          </div>
        </nav>
      </div>

      <div className="ab-shell ab-footer__bottom">
        <p>
          © {currentYear} AtadaBoard by Atada Technologies. All rights reserved.
        </p>

        <div className="ab-footer__social">
          {SOCIAL_LINKS.map((social) => {
            const Icon = SOCIAL_ICONS[social.icon];
            if (!Icon) return null;
            return (
              <a
                key={social.name}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`AtadaBoard on ${social.name}`}
              >
                <Icon size={18} strokeWidth={1.75} />
              </a>
            );
          })}
        </div>

        <button
          type="button"
          className="ab-footer__top-link"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          Back to top
          <ArrowUp size={15} strokeWidth={2} />
        </button>
      </div>

      <style jsx>{`
        .ab-footer {
          background: var(--ab-navy);
          color: rgba(255, 255, 255, 0.72);
          padding-top: clamp(3rem, 5vw, 4.5rem);
        }

        .ab-footer__top {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr);
          gap: clamp(2.5rem, 5vw, 5rem);
          padding-bottom: clamp(2.5rem, 4vw, 3.5rem);
        }

        .ab-footer__brand img {
          height: 30px;
          width: auto;
          display: block;
          /* The wordmark is solid brand blue; invert it to read on the navy. */
          filter: brightness(0) invert(1);
          margin-bottom: 1.125rem;
        }

        .ab-footer__brand p {
          font-size: 0.9375rem;
          line-height: 1.6;
          max-width: 38ch;
        }

        .ab-footer__phone {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 1.25rem;
          font-size: 1rem;
          font-weight: 600;
          color: #fff;
          text-decoration: none;
        }

        .ab-footer__phone:hover { color: #9ecbec; }

        .ab-footer__nav {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 2rem;
        }

        .ab-footer__group h2 {
          font-size: 0.8125rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.52);
          margin-bottom: 1rem;
        }

        .ab-footer__group ul {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 0.625rem;
        }

        .ab-footer__group a,
        .ab-footer__group button {
          display: inline-block;
          padding: 0;
          border: 0;
          background: none;
          font-family: var(--ab-font-body);
          font-size: 0.9375rem;
          /* 7.4:1 against the navy. */
          color: rgba(255, 255, 255, 0.78);
          text-align: left;
          text-decoration: none;
          cursor: pointer;
          transition: color 0.2s var(--ab-ease);
        }

        .ab-footer__group a:hover,
        .ab-footer__group button:hover { color: #fff; }

        .ab-footer__bottom {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 1.25rem;
          padding-block: 1.5rem;
          border-top: 1px solid rgba(255, 255, 255, 0.12);
          font-size: 0.875rem;
        }

        .ab-footer__social { display: flex; gap: 0.5rem; }

        .ab-footer__social a {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: var(--ab-r-pill);
          border: 1px solid rgba(255, 255, 255, 0.18);
          color: rgba(255, 255, 255, 0.78);
          transition: background-color 0.2s var(--ab-ease), color 0.2s var(--ab-ease),
            border-color 0.2s var(--ab-ease);
        }

        .ab-footer__social a:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.4);
          color: #fff;
        }

        .ab-footer__top-link {
          display: inline-flex;
          align-items: center;
          gap: 0.4375rem;
          background: none;
          border: 0;
          padding: 0;
          font-family: var(--ab-font-body);
          font-size: 0.875rem;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.78);
          cursor: pointer;
          transition: color 0.2s var(--ab-ease);
        }

        .ab-footer__top-link:hover { color: #fff; }

        .ab-footer a:focus-visible,
        .ab-footer button:focus-visible {
          outline: 2px solid #fff;
          outline-offset: 3px;
          border-radius: 4px;
        }

        @media (max-width: 860px) {
          .ab-footer__top { grid-template-columns: minmax(0, 1fr); gap: 2.5rem; }
        }

        @media (max-width: 480px) {
          .ab-footer__nav { grid-template-columns: minmax(0, 1fr); gap: 2rem; }
          .ab-footer__bottom { justify-content: flex-start; }
        }
      `}</style>
    </footer>
  );
};

export default Footer;
